import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import ContentTypes from '../../../../../constants/content-types';
import materialTypes from '../../../../../constants/materialTypes';
import { cn } from '../../../../../lib/utils';
import axios from '../../../../../services/http.service';

const MATERIAL_TYPE_OPTIONS = Object.keys(materialTypes);
const CONTENT_TYPE_OPTIONS = Object.keys(ContentTypes);

function SkillDetailForm({
  detail,
  updateDetails,
  removeDetails,
  addNewDetails,
  isLastItem,
  growLevels,
  nodeItemId,
  setIsDetailActive,
  isDetailActive,
  detailsAmount,
  setDetailsAmount,
}) {
  const [detailsNamesArray, setDetailsNamesArray] = useState([]);

  useEffect(() => {
    try {
      axios({
        method: 'GET',
        url: `head/grow/${nodeItemId}/details`,
      }).then((res) => {
        setDetailsNamesArray([...res]);
        setDetailsAmount(detailsAmount);
      });
    } catch (err) {
      console.error(err);
    }
  }, [nodeItemId]);

  const onBlur = () => {
    setIsDetailActive(!isDetailActive);
  };

  const handleBlur = useCallback(
    (e) => {
      const { currentTarget } = e;

      requestAnimationFrame(() => {
        if (!currentTarget.contains(document.activeElement)) {
          onBlur();
        }
      });
    },
    [onBlur],
  );

  const handleLevelChange = (levelId) => {
    updateDetails({
      ...detail,
      level: levelId,
    });
  };

  return (
    <div
      id={detail.itemId}
      className="p-4 rounded-lg bg-muted space-y-3 border"
      style={{ borderColor: 'hsl(var(--border))' }}
      onBlur={handleBlur}
    >
      <div className="grid grid-cols-2 gap-3">
        {/* Name - Autocomplete */}
        <Autocomplete
          freeSolo
          size="small"
          options={[...new Set(detailsNamesArray)]}
          value={detail.name}
          onInputChange={(event, newValue, reason) => {
            if (detail.name !== newValue) {
              updateDetails({
                ...detail,
                name: reason === 'clear' ? '' : newValue,
              });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'hsl(var(--background))',
                  fontSize: '0.875rem',
                },
              }}
            />
          )}
        />

        {/* Success Criteria */}
        <input
          type="text"
          value={detail.successCriteria}
          placeholder="Success Criteria"
          onChange={(e) =>
            updateDetails({
              ...detail,
              successCriteria: e.target.value,
            })
          }
          className={cn(
            'px-3 py-2 rounded-md text-sm',
            'bg-background border border-input',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
            'placeholder:text-muted-foreground',
          )}
        />

        {/* Material Name */}
        <input
          type="text"
          value={detail.materialName}
          placeholder="Material Name"
          onChange={(e) =>
            updateDetails({
              ...detail,
              materialName: e.target.value,
            })
          }
          className={cn(
            'px-3 py-2 rounded-md text-sm',
            'bg-background border border-input',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
            'placeholder:text-muted-foreground',
          )}
        />

        {/* Material Type */}
        <select
          value={detail.materialType}
          onChange={(e) =>
            updateDetails({
              ...detail,
              materialType: e.target.value,
            })
          }
          className={cn(
            'px-3 py-2 rounded-md text-sm',
            'bg-background border border-input',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
            !detail.materialType && 'text-muted-foreground',
          )}
        >
          <option value="" disabled>
            Material Type
          </option>
          {MATERIAL_TYPE_OPTIONS.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        {/* Link */}
        <input
          type="text"
          value={detail.link}
          placeholder="Link"
          onChange={(e) =>
            updateDetails({
              ...detail,
              link: e.target.value,
            })
          }
          className={cn(
            'px-3 py-2 rounded-md text-sm',
            'bg-background border border-input',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
            'placeholder:text-muted-foreground',
          )}
        />

        {/* Content Type */}
        <select
          value={detail.contentType || ''}
          onChange={(e) =>
            updateDetails({
              ...detail,
              contentType: e.target.value || null,
            })
          }
          className={cn(
            'px-3 py-2 rounded-md text-sm',
            'bg-background border border-input',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
            !detail.contentType && 'text-muted-foreground',
          )}
        >
          <option value="">Content Type (optional)</option>
          {CONTENT_TYPE_OPTIONS.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {/* Level Selection & Actions */}
      <div className="flex items-center justify-between pt-2">
        {/* Level Radio Buttons */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">Level:</span>
          <div className="flex items-center gap-2">
            {growLevels.map((level) => (
              <button
                key={level._id}
                type="button"
                onClick={() => handleLevelChange(level._id)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium transition-all',
                  detail.level === level._id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
              >
                {level.key}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {detailsAmount > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setDetailsNamesArray(detailsNamesArray.filter((el) => el !== detail.name));
                setDetailsAmount(detailsAmount - 1);
                removeDetails(detail);
              }}
              title="Remove detail"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {isLastItem && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setDetailsNamesArray([...detailsNamesArray, '']);
                setDetailsAmount(detailsAmount + 1);
                addNewDetails();
              }}
              title="Add detail"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

SkillDetailForm.propTypes = {
  detail: PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    successCriteria: PropTypes.string.isRequired,
    materialName: PropTypes.string.isRequired,
    materialType: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    level: PropTypes.string.isRequired,
    contentType: PropTypes.string,
  }).isRequired,
  updateDetails: PropTypes.func.isRequired,
  removeDetails: PropTypes.func.isRequired,
  addNewDetails: PropTypes.func.isRequired,
  isLastItem: PropTypes.bool.isRequired,
  nodeItemId: PropTypes.string.isRequired,
  growLevels: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      key: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isDetailActive: PropTypes.bool.isRequired,
  setIsDetailActive: PropTypes.func.isRequired,
  setDetailsAmount: PropTypes.func.isRequired,
  detailsAmount: PropTypes.number.isRequired,
};

export default SkillDetailForm;
